import clsx from "clsx";
import React, { useEffect, useMemo } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { shallowEqual, useSelector } from "react-redux";
import { simpleCommands } from "src/data/commands";
import { RootState } from "src/store";

import { selectDevicesByGroupId } from "@dashboard/devices/store/selectors/devices";
import { Device, Group } from "@dashboard/devices/types";
import { JSONEditor } from "@json-editor/json-editor/dist/jsoneditor.js";
import { Box, Typography, useTheme } from "@mui/material";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { showErrorToast } from "@shared/toasts/Toasts";

import { filterCommands } from "../helpers/filterCommands";
import { JsonEditorCommandType, updateEditor, validateEditor } from "../helpers/jsonEditor";
import { usePolicyCommands } from "../hooks";
import { FormValues } from "../NewCommandDialog";
import { InputItemContainer, useStyles } from "../styles";

import {
    buildExamplesCommandsList,
    getEffectiveEmteriaUpdateVersionCode,
    prepareCommandPayload,
} from "./CommandHelpers";

let simpleEditor: JSONEditor | null = null;

type Props = {
    submitRef: React.RefObject<HTMLButtonElement | null>;
    editorRef: React.RefObject<HTMLDivElement | null>;
    device?: Device;
    group?: Group;
    isPolicy?: boolean;
    toggleDialog: () => void;
    createCommand?: (json: Record<string, unknown>, commandId: number | null) => Promise<void>;
};

export const Examples = ({ device, group, submitRef, editorRef, isPolicy, toggleDialog, createCommand }: Props) => {
    const { t } = useTranslation(["modals"]);
    const { classes } = useStyles();
    const theme = useTheme();
    const policyCommands = group?.policy || [];
    const groupDevices = useSelector(
        (state: RootState) =>
            selectDevicesByGroupId(state, group?.id).filter((d): d is Device => d !== null),
        shallowEqual,
    );

    const { updatePolicyCommands } = usePolicyCommands();

    const examplesCommands = useMemo(
        () => buildExamplesCommandsList(filterCommands(simpleCommands, isPolicy), { device, groupDevices }),
        [isPolicy, device, groupDevices],
    );

    useEffect(() => {
        simpleEditor = updateEditor(
            simpleEditor,
            editorRef,
            JsonEditorCommandType.Examples,
            examplesCommands,
            examplesCommands[0].name,
        );

        // destroy the editor to prevent duplicates when component is unmounting
        return () => {
            if (simpleEditor) {
                simpleEditor.destroy();
            }
        };
    }, [examplesCommands, group, editorRef]);

    const onSubmit = async (values: FormValues) => {
        const errors = validateEditor(simpleEditor, editorRef.current);
        if (errors) {
            const firstError = errors[0] as { path?: string; message?: string };

            if (values.commandType === "startOsUpdate" && firstError?.path?.includes("version")) {
                showErrorToast(t("modals:newPolicyCommand.toast.versionRequired"));
                return;
            }

            return;
        }

        const input = simpleEditor && simpleEditor.getValue();
        if (!input) {
            showErrorToast(t("modals:newPolicyCommand.toast.commandContentEmpty"));
            return;
        }

        const commandTemplate = examplesCommands.find((obj) => obj.name === values.commandType)?.template;
        if (!commandTemplate) {
            showErrorToast(t("modals:newPolicyCommand.toast.commandTemplateNotFound"));
            return;
        }

        // Merge with the params from the template
        const basePayload = { ...commandTemplate, ...input };
        const emteriaUpdateVersionCode = getEffectiveEmteriaUpdateVersionCode({ device, groupDevices });
        const finalPayload = prepareCommandPayload(values.commandType, basePayload, emteriaUpdateVersionCode);

        if (isPolicy && group) {
            const policyPayload = Array.isArray(policyCommands) && [...policyCommands, finalPayload];

            await updatePolicyCommands({ groupId: group.id, policy: JSON.stringify(policyPayload) });
        } else if (createCommand) {
            await createCommand(finalPayload, null);
        }
        toggleDialog();
    };

    return (
        <React.Fragment>
            <Form<FormValues>
                onSubmit={onSubmit}
                initialValues={{ commandType: examplesCommands[0].name }}
                render={({ handleSubmit, form }) => (
                    <form onSubmit={handleSubmit} id="editCommand">
                        <div className={classes.sectionContainer}>
                            <InputItemContainer>
                                <Box className={clsx([classes.input, classes.select])}>
                                    <div className="form-group">
                                        <Typography style={{ marginBottom: theme.spacing(1) }} variant="subtitle1">
                                            <b>{t("modals:newPolicyCommand.commandType")}</b>
                                        </Typography>
                                        <ControlledSelectField
                                            name="commandType"
                                            className="form-control"
                                            searchable
                                            options={examplesCommands.map((obj) => ({
                                                value: obj.name,
                                                label: obj.schema.title,
                                            }))}
                                            onChange={() => {
                                                simpleEditor = updateEditor(
                                                    simpleEditor,
                                                    editorRef,
                                                    JsonEditorCommandType.Examples,
                                                    examplesCommands,
                                                    form.getFieldState("commandType")?.value ?? examplesCommands[0].name,
                                                );
                                            }}
                                            fullWidth
                                        />
                                    </div>
                                </Box>
                            </InputItemContainer>
                            <InputItemContainer style={{ paddingBottom: theme.spacing(4) }}>
                                <Typography className={classes.commandParametersLabel} variant="subtitle1">
                                    <b>{t("modals:newPolicyCommand.commandParameters")}</b>
                                </Typography>
                                <div id="json-editor" ref={editorRef}>
                                    {/* filled out by jsoneditor */}
                                </div>
                            </InputItemContainer>
                            <button ref={submitRef} type="submit" hidden />
                        </div>
                    </form>
                )}
            />
        </React.Fragment>
    );
};
